// src/utils/loadCachedData.js
import { fetchWithAuth } from "./fetchWithAuth";
import { notifyError } from "./notify";

// 🕒 Cache- & Meta-Einstellungen
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 Stunden gültig
const META_TTL_MS = 30 * 1000; // 30 Sekunden RAM-Cache für /meta
const COOLDOWN_MS = 4000; // 4 Sekunden Sperrzeit nach Reload

let metaCache = null;
let metaCacheExpiry = 0;

/**
 * Holt die /meta/last-updates vom Backend mit RAM-Caching (30s)
 */
async function getMetaCached(backendUrl, navigate, { frisch = false } = {}) {
  const now = Date.now();
  // `frisch` umgeht den 30-Sekunden-RAM-Cache. Wird beim Zurueckkehren auf den Tab
  // genutzt, damit die sofortige Pruefung nicht auf veralteten Meta-Daten arbeitet.
  if (!frisch && metaCache && now < metaCacheExpiry) return metaCache;

  try {
    const data = await fetchWithAuth(`${backendUrl}/meta/last-updates`, navigate);

    if (!data || typeof data !== "object") {
      console.warn("[MetaCache] Ungültige Meta-Daten erhalten → Fallback zu leerem Objekt");
      metaCache = {};
      metaCacheExpiry = now + META_TTL_MS;
      return {};
    }

    metaCache = data;
    metaCacheExpiry = now + META_TTL_MS;
    return data;
  } catch (err) {
    console.error("[Cache] Fehler beim Abrufen der Meta-Daten:", err);
    // Bei Auth-Fehlern NICHT {} zurueckgeben: das wuerde vom MetaWatcher als
    // "alles auf 0 geaendert" gewertet und einen sinnlosen Reload-Versuch
    // ausloesen (fetchWithAuth kuemmert sich bereits um Logout/Redirect).
    throw err;
  }
}

/**
 * Holt Cache aus localStorage, prüft Ablaufzeit
 */
function getCache(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const parsed = JSON.parse(cached);

    // TTL-Prüfung
    if (!parsed.timestamp || Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      console.log(`[Cache][EXPIRED] ${key} → abgelaufen`);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Schreibt Daten mit aktuellem Timestamp in den Cache.
 * Leere Arrays werden nicht gecached (verhindert Cache Poisoning).
 */
function setCache(key, data, metaKeyValue) {
  if (!data || (Array.isArray(data) && data.length === 0)) {
    console.warn(`[Cache][SKIP] ${key} nicht gespeichert (leer oder ungültig)`);
    return;
  }

  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        data,
        _meta: metaKeyValue,
        timestamp: Date.now(),
      })
    );
  } catch (err) {
    console.warn(`[Cache][ERROR] ${key} konnte nicht gesetzt werden:`, err);
  }
}

/**
 * Löscht Cache-Eintrag (exportiert, falls extern benötigt)
 */
export function clearCache(key) {
  try {
    localStorage.removeItem(key);
    console.log(`[Cache][CLEAR] ${key} gelöscht`);
  } catch (err) {
    console.warn(`[Cache][ERROR] Fehler beim Löschen von ${key}:`, err);
  }
}

/**
 * Alle Daten-Caches dieses Geraets leeren – bei Anmeldung und Abmeldung.
 *
 * Der Cache ist nur an den Meta-Stempel gebunden, nicht an die Rolle. Auf einem
 * Geraet, an dem vorher der Admin angemeldet war, laegen sonst dessen Daten
 * (vollstaendiges Ranking mit Gesamtpunkten) weiter im localStorage und wuerden
 * nach einer Betreuer-Anmeldung sogar als gueltiger Cache weiterverwendet.
 * Die Offline-Warteschlange der Eintraege bleibt bewusst unberuehrt.
 */
export function alleDatenCachesLeeren() {
  ["teams", "stations", "results"].forEach(clearCache);
  metaCache = null;
  metaCacheExpiry = 0;
}

/**
 * Hybrid-Datenlader für Teams, Stationen und Ergebnisse.
 * Lädt nur neu, wenn laut Meta etwas geändert wurde oder reloadFlags aktiv sind.
 */
export async function loadCachedTeamsAndStations(
  backendUrl,
  navigate,
  { reloadTeams = false, reloadStations = false, reloadResults = false } = {}
) {
  const CACHE_KEYS = { teams: "teams", stations: "stations", results: "results" };
  const reloadOptions = { reloadTeams, reloadStations, reloadResults };

  try {
    const meta = await getMetaCached(backendUrl, navigate);
    const metaValues = {
      teams: meta?.teams ?? 0,
      stations: meta?.stations ?? 0,
      results: meta?.results ?? 0,
    };

    const cached = {
      teams: getCache(CACHE_KEYS.teams),
      stations: getCache(CACHE_KEYS.stations),
      results: getCache(CACHE_KEYS.results),
    };

    const validity = {
      teams: cached.teams && cached.teams._meta === metaValues.teams,
      stations: cached.stations && cached.stations._meta === metaValues.stations,
      results: cached.results && cached.results._meta === metaValues.results,
    };

    // Nur das laden, was nötig ist
    const tasks = {};
    for (const key of Object.keys(CACHE_KEYS)) {
      const shouldReload = reloadOptions[`reload${key.charAt(0).toUpperCase() + key.slice(1)}`];
      tasks[key] =
        validity[key] && !shouldReload
          ? cached[key].data
          : fetchWithAuth(`${backendUrl}/${key}`, navigate);
    }

    // 🔄 Parallel laden / auflösen
    const [teams, stations, results] = await Promise.all([
      tasks.teams,
      tasks.stations,
      tasks.results,
    ]);

    // Cache aktualisieren, falls neu geladen
    if (!validity.teams || reloadTeams) setCache(CACHE_KEYS.teams, teams, metaValues.teams);
    if (!validity.stations || reloadStations) setCache(CACHE_KEYS.stations, stations, metaValues.stations);
    if (!validity.results || reloadResults) setCache(CACHE_KEYS.results, results, metaValues.results);

    // Prüfen, ob alle Kerndaten vollständig sind
    const fromCache =
      validity.teams &&
      validity.stations &&
      validity.results &&
      !reloadTeams &&
      !reloadStations &&
      !reloadResults;

    const hasData =
      Array.isArray(teams) && teams.length > 0 &&
      Array.isArray(stations) && stations.length > 0;

    // 🧩 Cold-Start-Fallback
    if (fromCache && !hasData) {
      console.warn("[Cache][RELOAD] Leere Kerndaten trotz gültigem Cache → erzwungener Reload");

      // Cache löschen, um Schleifen zu verhindern
      clearCache(CACHE_KEYS.teams);
      clearCache(CACHE_KEYS.stations);

      return await loadCachedTeamsAndStations(backendUrl, navigate, {
        reloadTeams: true,
        reloadStations: true,
        reloadResults: true,
      });
    }

    // 🔍 Logging
    console.info(
      `[Cache][${fromCache ? "HIT" : "MISS"}] → Teams: ${teams?.length || 0}, Stations: ${
        stations?.length || 0
      }, Results: ${results?.stations?.length || results?.length || 0}`
    );

    return { teams, stations, results, fromCache };
  } catch (err) {
    console.error("[Cache][ERROR] Fehler beim Laden:", err);
    notifyError("Fehler beim Laden der Daten");
    return { teams: [], stations: [], results: [], fromCache: false };
  }
}

/**
 * Periodischer Meta-Check.
 * Erkennt Änderungen an Teams, Stationen, Ergebnissen und ruft onChange() differenziert auf.
 */
export function startMetaWatcher(
  backendUrl,
  navigate,
  { onResultsChange, onDataChange, onChange },
  intervalMs = 120000 // 2 Minuten
) {
  let lastMeta = { teams: 0, stations: 0, results: 0 };
  let lastReloadAt = 0;
  // Die erste Abfrage dient nur als Bezugspunkt. Ohne dieses Flag gilt jeder
  // echte Zeitstempel als Aenderung gegenueber dem Startwert 0 – jede Seite
  // wuerde zwei Minuten nach dem Laden grundlos neu laden.
  let ersterLauf = true;
  let laeuft = false; // verhindert Ueberlappung von Intervall und checkNow()

  async function pruefe({ frisch = false } = {}) {
    if (laeuft) return;
    laeuft = true;
    try {
      const meta = await getMetaCached(backendUrl, navigate, { frisch });
      const metaTeams = meta?.teams ?? 0;
      const metaStations = meta?.stations ?? 0;
      const metaResults = meta?.results ?? 0;

      if (ersterLauf) {
        ersterLauf = false;
        lastMeta = { teams: metaTeams, stations: metaStations, results: metaResults };
        console.log("[MetaWatcher] Bezugspunkt gesetzt:", lastMeta);
        return;
      }

      const now = Date.now();
      const cooldownActive = now - lastReloadAt < COOLDOWN_MS;

      const teamsChanged = metaTeams !== lastMeta.teams;
      const stationsChanged = metaStations !== lastMeta.stations;
      const resultsChanged = metaResults !== lastMeta.results;

      if ((teamsChanged || stationsChanged || resultsChanged) && !cooldownActive) {
        lastMeta = { teams: metaTeams, stations: metaStations, results: metaResults };
        lastReloadAt = now;

        const changed = { teams: teamsChanged, stations: stationsChanged, results: resultsChanged };
        console.log("[MetaWatcher] Änderung erkannt:", changed);

        if (typeof onChange === "function") onChange(changed);

        // Rückwärtskompatibilität
        if ((teamsChanged || stationsChanged) && typeof onDataChange === "function") {
          onDataChange();
        } else if (resultsChanged && typeof onResultsChange === "function") {
          onResultsChange();
        }
      }
    } catch (err) {
      console.warn("[MetaWatcher] Fehler beim Abfragen:", err);
    } finally {
      laeuft = false;
    }
  }

  const interval = setInterval(() => {
    // Im Hintergrund keine Requests feuern (spart Aufrufe und Kosten). Der Watcher
    // wird dabei NICHT abgebaut – so bleibt `lastMeta` als Bezugspunkt erhalten
    // und eine Aenderung waehrend der Abwesenheit geht nicht verloren.
    if (typeof document !== "undefined" && document.hidden) return;
    pruefe();
  }, intervalMs);

  const stop = () => clearInterval(interval);

  // Sofortige Pruefung, z. B. wenn der Tab wieder sichtbar wird. Vergleicht gegen
  // den erhaltenen Bezugspunkt und meldet nur echte Aenderungen.
  stop.checkNow = () => pruefe({ frisch: true });

  return stop;
}
